/**
 * A clickable table header that drives server-side sorting.
 *
 * Lifted out of `ConstructionDeadlinesPage` when a second page needed it. Two things
 * it gained on the way out, both of which it needed to be shared and arguably needed
 * anyway:
 *
 * - **`aria-sort` on the `<th>`.** Without it a screen reader announces a column
 *   header with an arrow glyph next to it and no statement of what is sorted.
 * - **A real `<button>` inside.** The original put `onClick` on the `<th>`, which is
 *   not focusable and not operable from the keyboard — the sort was mouse-only.
 */
import { ReactNode } from 'react'

interface SortableHeaderProps<K extends string> {
  children: ReactNode
  sortKey: K
  currentSortKey: K
  currentSortOrder: 'asc' | 'desc'
  onSort: (key: K) => void
  className?: string
  /**
   * The column cannot be sorted right now — render a plain, non-interactive header.
   *
   * Added for the license search, where a radius search always orders by distance and the
   * backend ignores `sort_by` entirely (`licenses.py:620`). The header still painted an
   * active arrow on whatever you clicked, so the table claimed an ordering it did not
   * have. A control that does nothing should not look like a control.
   */
  disabled?: boolean
  /** Why it is disabled — shown on hover, so the greying-out is explained rather than odd. */
  disabledReason?: string
}

export function SortableHeader<K extends string>({
  children,
  sortKey,
  currentSortKey,
  currentSortOrder,
  onSort,
  className,
  disabled = false,
  disabledReason,
}: SortableHeaderProps<K>) {
  const isActive = currentSortKey === sortKey

  if (disabled) {
    // No button, no arrow, and aria-sort="none" — the column genuinely is not sorted, and
    // announcing otherwise would be the same lie in a different medium.
    return (
      <th className={className} aria-sort="none" title={disabledReason}>
        <span className="text-gray-400">{children}</span>
      </th>
    )
  }

  return (
    <th
      className={className}
      aria-sort={isActive ? (currentSortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="tap gap-1 select-none font-medium hover:text-gray-700"
      >
        {children}
        {/* aria-hidden: the arrow repeats what aria-sort already announces. */}
        <span className="text-gray-400" aria-hidden="true">
          {isActive ? (currentSortOrder === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}
